import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    Card,
    Tabs,
    Spin,
    Alert,
    Button,
    Space,
    Row,
    Col
} from 'antd';
import {
    CreditCardOutlined,
    FileTextOutlined,
    RocketOutlined,
    CrownOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    ThunderboltOutlined,
    GiftOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetSubscriptionsQuery,
    useGetPaymentMethodsQuery,
    useGetInvoicesQuery,
    useLazyGetInvoicesQuery,
    useGetBrandsQuery,
    useGetOrganisationSubscriptionQuery
} from '../../services/nestjs-base-api';
import { SubscriptionPlanEnum, SubscriptionStatusEnum, IInvoice } from '../../services/nestjs-base-api/types';
import PaymentMethods from './PaymentMethods';
import Invoices from './Invoices';
import SubscriptionOverview from './SubscriptionOverview';
import UpgradeToProfessionalModal from './UpgradeToProfessionalModal';
import OrganisationAddons from './OrganisationAddons';

interface IBillingProps {
    showUpgradeOnMount?: boolean;
}

const Billing: React.FunctionComponent<IBillingProps> = ({ showUpgradeOnMount }) => {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    // Check for subtab query parameter
    const searchParams = new URLSearchParams(location.search);
    const subtabParam = searchParams.get('subtab');
    const [activeTab, setActiveTab] = useState(subtabParam || 'overview');
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    // Update active tab when subtab param changes
    useEffect(() => {
        if (subtabParam) {
            setActiveTab(subtabParam);
        }
    }, [subtabParam]);

    const organisationId = user?.organisation?.id;
    const jwtToken = user?.jwtToken;

    // Track if we've already processed the URL triggers (to prevent re-triggering)
    const [urlTriggersProcessed, setUrlTriggersProcessed] = useState(false);

    // Handle showUpgradeOnMount prop - always respect this prop when it becomes true
    useEffect(() => {
        if (showUpgradeOnMount) {
            setShowUpgradeModal(true);
        }
    }, [showUpgradeOnMount]);

    // Handle URL-based triggers separately (hash, state, query param)
    // Sources: URL hash (#upgrade), location.state.showUpgradeModal, or ?showUpgrade=true query param
    useEffect(() => {
        // Skip if we've already processed URL triggers on this page load
        if (urlTriggersProcessed) return;

        const locationState = location.state as { showUpgradeModal?: boolean } | null;
        const searchParams = new URLSearchParams(location.search);
        const hasHash = location.hash === '#upgrade';
        const hasState = locationState?.showUpgradeModal === true;
        const hasQueryParam = searchParams.get('showUpgrade') === 'true';

        if (hasHash || hasState || hasQueryParam) {
            setShowUpgradeModal(true);
            setUrlTriggersProcessed(true);
        }
    }, [location.hash, location.key, location.state, location.pathname, location.search, urlTriggersProcessed]);

    // Clean up URL when modal is closed (if there were URL triggers)
    const handleCloseUpgradeModal = () => {
        setShowUpgradeModal(false);
        // Clean up URL if needed
        const searchParams = new URLSearchParams(location.search);
        if (location.hash === '#upgrade' || searchParams.get('showUpgrade') === 'true') {
            searchParams.delete('showUpgrade');
            const newSearch = searchParams.toString();
            const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
            navigate(newUrl, { replace: true, state: {} });
        }
    };

    const { 
        data: subscriptionsData, 
        isLoading: subscriptionsLoading, 
        error: subscriptionsError 
    } = useGetSubscriptionsQuery(
        { jwtToken: jwtToken!, organisationId: organisationId! },
        { skip: !jwtToken || !organisationId }
    );

    const { 
        data: brandsData, 
        isLoading: brandsLoading 
    } = useGetBrandsQuery(
        { jwtToken: jwtToken!, organisationId: organisationId! },
        { skip: !jwtToken || !organisationId }
    );

    const { 
        data: paymentMethodsData, 
        isLoading: paymentMethodsLoading 
    } = useGetPaymentMethodsQuery(
        { jwtToken: jwtToken!, organisationId: organisationId! },
        { skip: !jwtToken || !organisationId }
    );

    const {
        data: invoicesData,
        isLoading: invoicesLoading
    } = useGetInvoicesQuery(
        { jwtToken: jwtToken!, organisationId: organisationId!, limit: 25 },
        { skip: !jwtToken || !organisationId }
    );

    // State for accumulated invoices and pagination
    const [allInvoices, setAllInvoices] = useState<IInvoice[]>([]);
    const [hasMoreInvoices, setHasMoreInvoices] = useState(false);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [loadMoreInvoices, { isLoading: loadingMoreInvoices }] = useLazyGetInvoicesQuery();

    // Update accumulated invoices when initial data loads
    useEffect(() => {
        if (invoicesData) {
            setAllInvoices(invoicesData.data);
            setHasMoreInvoices(invoicesData.hasMore);
            setNextCursor(invoicesData.nextCursor);
        }
    }, [invoicesData]);

    // Handler to load more invoices
    const handleLoadMoreInvoices = useCallback(async () => {
        if (!jwtToken || !organisationId || !nextCursor || loadingMoreInvoices) return;

        try {
            const result = await loadMoreInvoices({
                jwtToken,
                organisationId,
                limit: 25,
                cursor: nextCursor,
            }).unwrap();

            setAllInvoices(prev => [...prev, ...result.data]);
            setHasMoreInvoices(result.hasMore);
            setNextCursor(result.nextCursor);
        } catch (error) {
            console.error('Failed to load more invoices:', error);
        }
    }, [jwtToken, organisationId, nextCursor, loadingMoreInvoices, loadMoreInvoices]);

    // Fetch organization subscription (different from Stripe subscriptions)
    const {
        data: orgSubscriptionData,
        isLoading: orgSubscriptionLoading
    } = useGetOrganisationSubscriptionQuery(
        { jwtToken: jwtToken!, orgId: String(organisationId!) },
        { skip: !jwtToken || !organisationId }
    );

    const subscriptions = useMemo(() => subscriptionsData || [], [subscriptionsData]);
    const brands = useMemo(() => brandsData || [], [brandsData]);
    const paymentMethods = paymentMethodsData || [];

    // Get organization-level subscription (brandId is null or matches first brand)
    const organizationSubscription = useMemo(() => {
        return subscriptions.find(sub => !sub.brandId || sub.brandId === brands[0]?.id);
    }, [subscriptions, brands]);

    const getStatusConfig = (status: SubscriptionStatusEnum) => {
        const configs: Record<SubscriptionStatusEnum, { color: string; text: string; icon: React.ReactNode }> = {
            [SubscriptionStatusEnum.Active]: { color: '#52c41a', text: 'Active', icon: <CheckCircleOutlined /> },
            [SubscriptionStatusEnum.Trialing]: { color: '#fa8c16', text: 'Trial', icon: <ClockCircleOutlined /> },
            [SubscriptionStatusEnum.PastDue]: { color: '#ff4d4f', text: 'Past Due', icon: <WarningOutlined /> },
            [SubscriptionStatusEnum.Canceled]: { color: '#d9d9d9', text: 'Canceled', icon: <WarningOutlined /> },
            [SubscriptionStatusEnum.Unpaid]: { color: '#ff4d4f', text: 'Unpaid', icon: <WarningOutlined /> },
            [SubscriptionStatusEnum.Incomplete]: { color: '#ff4d4f', text: 'Incomplete', icon: <WarningOutlined /> },
        };
        return configs[status] || { color: '#d9d9d9', text: status, icon: <WarningOutlined /> };
    };

    const getPlanConfig = (plan: SubscriptionPlanEnum) => {
        const configs = {
            [SubscriptionPlanEnum.Professional]: { 
                color: '#1890ff', 
                icon: <RocketOutlined />, 
                name: 'Professional',
                features: ['Up to 5 brands', 'Advanced analytics', 'API access', 'Priority support']
            },
            [SubscriptionPlanEnum.Enterprise]: { 
                color: '#722ed1', 
                icon: <CrownOutlined />, 
                name: 'Enterprise',
                features: ['Unlimited brands', 'Custom integrations', 'Dedicated support', 'SLA guarantee']
            },
        };
        return configs[plan] || { 
            color: '#52c41a', 
            icon: <ThunderboltOutlined />, 
            name: plan,
            features: []
        };
    };

    const handleStartSubscription = () => {
        setShowUpgradeModal(true);
    };

    if (subscriptionsLoading || brandsLoading || paymentMethodsLoading || invoicesLoading || orgSubscriptionLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>Loading billing information...</div>
            </div>
        );
    }

    if (subscriptionsError) {
        return (
            <Alert
                message="Error loading billing information"
                description="Please try refreshing the page or contact support if the problem persists."
                type="error"
                showIcon
            />
        );
    }

    // Check if subscription is in an active state (not canceled)
    const activeStatuses = ['active', 'trialing', 'past_due', 'founding_cohort'];
    const isSubscriptionActive = orgSubscriptionData?.status && activeStatuses.includes(orgSubscriptionData.status);

    // Check if user is on Free plan (no subscription, FREE plan code, or canceled subscription)
    const isFree = !orgSubscriptionData?.plan?.code ||
        orgSubscriptionData.plan.code === 'FREE' ||
        !isSubscriptionActive;

    const overviewContent = (
        <div>
            {/* Subscription Overview - always show the unified SubscriptionOverview component */}
            <div style={{ marginBottom: 24 }}>
                {organisationId && (
                    <SubscriptionOverview
                        organisationId={String(organisationId)}
                        onUpgrade={handleStartSubscription}
                        disableUpgradeModal={true}
                    />
                )}
            </div>

            {/* Quick Actions - always shown */}
            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                <Col span={24}>
                    <Card
                        title={
                            <Space>
                                <ThunderboltOutlined style={{ color: '#F59E0B' }} />
                                <span>Quick Actions</span>
                            </Space>
                        }
                    >
                        <Space size={16} wrap>
                            {/* Only show Upgrade if on Free plan */}
                            {isFree && (
                                <Button
                                    type="default"
                                    icon={<RocketOutlined />}
                                    onClick={() => setShowUpgradeModal(true)}
                                >
                                    Upgrade Plan
                                </Button>
                            )}
                            {/* Only show Browse Add-ons for non-free users with active subscription */}
                            {!isFree && (
                                <Button
                                    type="default"
                                    icon={<GiftOutlined />}
                                    onClick={() => setActiveTab('addons')}
                                >
                                    Browse Add-ons
                                </Button>
                            )}
                            <Button
                                type="default"
                                icon={<CreditCardOutlined />}
                                onClick={() => setActiveTab('payment-methods')}
                            >
                                Payment Methods
                            </Button>
                            <Button
                                type="default"
                                icon={<FileTextOutlined />}
                                onClick={() => setActiveTab('invoices')}
                            >
                                View Invoices
                            </Button>
                        </Space>
                    </Card>
                </Col>
            </Row>
        </div>
    );

    const tabItems = [
        {
            key: 'overview',
            label: 'Overview',
            children: overviewContent,
        },
        // Only show Add-ons tab for non-free users with active subscription (free users can't access AI Experts)
        ...(!isFree ? [{
            key: 'addons',
            label: 'Add-ons',
            children: organisationId ? <OrganisationAddons organisationId={organisationId} /> : null,
        }] : []),
        {
            key: 'payment-methods',
            label: 'Payment Methods',
            children: <PaymentMethods paymentMethods={paymentMethods} />,
        },
        {
            key: 'invoices',
            label: 'Invoices',
            children: (
                <Invoices
                    invoices={allInvoices}
                    hasMore={hasMoreInvoices}
                    onLoadMore={handleLoadMoreInvoices}
                    isLoadingMore={loadingMoreInvoices}
                />
            ),
        },
    ];

    return (
        <div>
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                size="large"
            />

            {/* Upgrade Modal - triggered by #upgrade hash, query param, or location state */}
            <UpgradeToProfessionalModal
                visible={showUpgradeModal}
                onClose={handleCloseUpgradeModal}
                onUpgrade={handleStartSubscription}
            />
        </div>
    );
};

export default Billing;